import { DispositionsForm, DispositionsFormType, EventPanelOptions } from '@mark43/cad-api';
import { BlankModal, ButtonGroup, ModalBody, ModalFooter, ModalHeader } from '@cad-arc';
import React from 'react';
import { useForm } from 'react-hook-form';
import { FormBody } from '../forms/integrated-form-components/form-body';
import { FormCancelButton } from '../forms/integrated-form-components/form-cancel-button';
import { FormSaveButton } from '../forms/integrated-form-components/form-save-button';
import { IntegratedFormProvider } from '../forms/integrated-form-provider';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { ConstrainedModalProps } from '../modals/types';
import { UPDATE_DISPOSITION_DEFAULT_FORM_VALUES } from '../dispositions/constants';
import { useDispositionQuery } from '../dispositions/hooks/use-disposition-query';
import { useUpdateDispositionMutation } from '../dispositions/hooks/use-update-disposition-mutation';
import { DispositionForm } from '../dispositions/components/disposition-form';
import { RefreshEventPanel } from './refresh-event-panel';

export type UpdateDispositionModalComponentType = typeof UpdateDispositionModal;
export function UpdateDispositionModal(
    props: {
        eventId: number;
        unitId?: number;
        isPrimaryUnit?: boolean;
        eventPanelOptions: EventPanelOptions;
        refreshEventPanel: RefreshEventPanel;
        agencyId: number;
    } & ConstrainedModalProps
): React.ReactElement {
    const modalControls = useFullscreenModalControls();
    const onClose = props.onClose ?? modalControls.close;

    const dispositionQuery = useDispositionQuery({ agencyEventId: props.eventId, enabled: true });
    const updateDispositionMutation = useUpdateDispositionMutation({
        actingTransitioner: 'DISPATCHER',
        unitIds: props.unitId ? [props.unitId] : undefined,
        onMutationSuccess: (res) => {
            const mutationSuccessData = res?.data?.data;
            const eventPanelView = mutationSuccessData?.eventPanelView;
            if (eventPanelView) {
                props.refreshEventPanel({ eventPanelView });
            }
            onClose();
        },
    });
    const { isFetching, data } = dispositionQuery;

    const dispositionUnitForm = useForm<DispositionsFormType>({
        defaultValues: {
            ...UPDATE_DISPOSITION_DEFAULT_FORM_VALUES,
        },
    });

    const initialValues: DispositionsForm = {
        ...data?.form,
        agencyEventId: props.eventId,
        additionalDispositions: data?.form.additionalDispositions ?? [],
        requestingUnitId: props.unitId,
        isRequestingUnitPrimaryUnit: props.isPrimaryUnit,
    };

    return (
        <IntegratedFormProvider
            query={dispositionQuery}
            mutation={updateDispositionMutation}
            form={dispositionUnitForm}
        >
            <BlankModal
                isOpen={true}
                onClose={onClose}
                portalProps={props.portalProps}
                constrainToPortal={true}
                closeOnOverlayClick={props.closeOnOverlayClick}
            >
                <ModalHeader>Disposition</ModalHeader>
                <ModalBody>
                    <FormBody>
                        <DispositionForm
                            isLoading={isFetching}
                            agencyId={props.agencyId}
                            dispositionOptions={props.eventPanelOptions.dispositionOptions}
                            isPrimaryUnit={props.isPrimaryUnit}
                            initialValues={initialValues}
                        />
                    </FormBody>
                </ModalBody>
                <ModalFooter>
                    <ButtonGroup>
                        <FormCancelButton
                            onClick={() => {
                                onClose();
                            }}
                        />
                        <FormSaveButton>Save</FormSaveButton>
                    </ButtonGroup>
                </ModalFooter>
            </BlankModal>
        </IntegratedFormProvider>
    );
}
